import type { Row, TableOptions } from '../../hooks/useTable/types.ts';
import type { DataTableV2RowData } from '../../public.api.ts';

/**
 * Row identification extension props.
 * @public
 */
export interface DataTableV2RowIdentification<
  TData extends DataTableV2RowData,
> {
  /**
   * Enables identification of rows independent of their index in the array.
   * @public
   */
  rowId?: (row: TData) => string;
}

/**
 * Extension interface for the Tanstack Table options.
 * @internal
 */
export interface DataTableV2RowIdentificationOptions {
  rowIdSeparator?: string;
}

/**
 * Constant by which the row ids will be joined when using a custom row id
 * in combination with subrows.
 * @public
 */
export const DATATABLEV2_ROW_SEPARATION = '↳';

/**
 * Configuration hook for the rowInteractivity in the DataTableV2
 * @internal
 */
export function useRowIdentification<TData extends DataTableV2RowData>(
  props: DataTableV2RowIdentification<TData>,
  options: TableOptions<TData>,
) {
  const rowIdentificationFn = props.rowId;

  if (rowIdentificationFn) {
    options.rowIdSeparator = DATATABLEV2_ROW_SEPARATION;
    options.getRowId = (row: TData, index: number, parent?: Row<TData>) => {
      const rowId = rowIdentificationFn(row);
      if (parent) {
        const parentId = parent.id;
        return `${parentId}${options.rowIdSeparator}${rowId}`;
      }
      return rowId;
    };
  } else {
    options.getRowId = undefined;
  }
}
